import { useEffect, useState } from 'react';

import type { TaskInventory, VehicleType } from '@bringg/types';
import type { Row } from 'react-table';
import { calculateTaskInventoriesMaxLoad } from '@bringg-frontend/shared-tasks-weight';

import type { RoutesPlannerRun, RoutesPlannerTask } from '../../../types';
import useStore from '../../../stores/helpers/use-context';
import {
	DynamicAggregatedValues,
	getDynamicAggregationValues
} from '../../../services/dynamic-aggregations/dynamic-aggregations';

interface Props {
	tasksInventories: TaskInventory[];
	tasks?: Row<RoutesPlannerTask>[];
	routeId?: number;
	run?: RoutesPlannerRun;
}

export interface HandlingUnitsMap {
	maxValue: number;
	totalCapacity: number;
	dynamicAggregatedValues?: DynamicAggregatedValues;
}

const NO_HANDLING_UNIT_VALUE = -1;

const useRunHandlingUnits = ({ tasksInventories, tasks, routeId, run }: Props) => {
	const [handlingUnitsMap, setHandlingUnitsMap] = useState<Map<string, HandlingUnitsMap>>(new Map());
	const routesPlannerStore = useStore();
	const { handlingUnits, ignoreQuantityInLoadCalculation, uniqueInventoryFields, dynamicCapacityMode } =
		routesPlannerStore.allConfiguration || {};

	const route = routesPlannerStore.routesById[routeId];
	useEffect(() => {
		const validForRunAggregations = run && !routesPlannerStore.isRunModified(run.id);
		const handlingUnitsUpdateMap = new Map();
		handlingUnits &&
			Object.keys(handlingUnits).forEach(unitName =>
				updateHandlingUnitsMap(handlingUnitsUpdateMap, unitName, run, validForRunAggregations)
			);
		setHandlingUnitsMap(handlingUnitsUpdateMap);
	}, [handlingUnits]);

	const updateHandlingUnitsMap = (
		handlingUnitsUpdateMap: Map<string, HandlingUnitsMap>,
		unitName: string,
		run: RoutesPlannerRun,
		isUseRunAggregations: boolean
	) => {
		const field = `handling_units.${unitName}`;
		const runMaxValue = run?.max_handling_units?.[unitName];
		const maxValue =
			isUseRunAggregations && !!runMaxValue
				? runMaxValue
				: calculateTaskInventoriesMaxLoad(
						tasksInventories,
						field,
						ignoreQuantityInLoadCalculation,
						uniqueInventoryFields
				  );
		const runCapacityValue = run?.handling_units_capacity?.[unitName];
		const dynamicAggregatedValues: DynamicAggregatedValues = getDynamicAggregationValues(
			run,
			dynamicCapacityMode,
			field
		);
		handlingUnitsUpdateMap.set(unitName, {
			maxValue,
			totalCapacity: runCapacityValue ? runCapacityValue : tasks?.length ? getCapacity(unitName) : 0,
			dynamicAggregatedValues
		});
	};

	const getVehiclesTypeList = (): VehicleType[] => {
		const vehicleTypes = [];

		tasks.forEach(rowTask => {
			const taskId = rowTask.original.id;
			const task = routesPlannerStore.tasksById.get(taskId);
			task?.vehicle_id && vehicleTypes.push(routesPlannerStore.vehicleTypeByVehicleIds[task.vehicle_id]);
		});

		if (route?.vehicle_id) {
			vehicleTypes.push(routesPlannerStore.vehicleTypeByVehicleIds[route.vehicle_id]);
		}
		return [...new Set(vehicleTypes)].filter(Boolean);
	};

	const getCapacity = (handlingUnitsPath: string) => {
		const vehiclesTypesList = getVehiclesTypeList();

		let sumOfMaxHandlingUnits = 0;

		if (vehiclesTypesList.length) {
			vehiclesTypesList.forEach(vehicleType => {
				sumOfMaxHandlingUnits += vehicleType.handling_units?.[handlingUnitsPath] || 0;
			});
			return sumOfMaxHandlingUnits;
		}
		return NO_HANDLING_UNIT_VALUE;
	};

	return handlingUnitsMap;
};

export default useRunHandlingUnits;
